import { API_URL } from '../config/api';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface PendingRequest {
  url: string;
  init: RequestInit;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  parseAsJson: boolean;
}

const DEFAULT_TIMEOUT_MS = 20000;
const DEFAULT_RETRY_ATTEMPTS = 3;

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function buildHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

class HttpClient {
  // Храним только последний запрос на ключ method+url
  private offlineQueue: Map<string, PendingRequest> = new Map();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.flushQueue());
    }
  }

  private async fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...init, signal: controller.signal, credentials: 'include' });
      return response;
    } finally {
      clearTimeout(timeout);
    }
  }

  private async perform(url: string, init: RequestInit, parseAsJson = true, options?: { treat400AsData?: boolean, dropIfOffline?: boolean }): Promise<any> {
    const doRequest = async (): Promise<Response> => {
      return this.fetchWithTimeout(url, init, DEFAULT_TIMEOUT_MS);
    };

    // Если офлайн — либо отбрасываем запрос, либо сохраняем только последний на ключ method+url
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      if (options?.dropIfOffline) {
        return Promise.reject(new Error('Offline'));
      }
      const key = `${(init.method || 'GET').toUpperCase()} ${url}`;
      return new Promise((resolve, reject) => {
        const existing = this.offlineQueue.get(key);
        if (existing) {
          // Отменяем предыдущий такой же запрос, он устарел
          existing.reject(new Error('Superseded by newer offline request'));
        }
        this.offlineQueue.set(key, { url, init, resolve, reject, parseAsJson });
      });
    }

    let lastError: any;
    for (let attempt = 0; attempt < DEFAULT_RETRY_ATTEMPTS; attempt++) {
      try {
        const response = await doRequest();

        if (response.status === 401) {
          // Не обрабатываем 401 для эндпоинтов логина и регистрации - это нормальные ошибки при неверных данных
          const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
          if (!isAuthEndpoint) {
            // Сообщаем приложению о необходимости выхода только для других эндпоинтов
            const event = new CustomEvent('force-logout', { detail: { reason: 'unauthorized', timestamp: Date.now() } });
            document.dispatchEvent(event);
          }
          throw new Error('Unauthorized');
        }

        if (!response.ok) {
          if (response.status === 400 && options?.treat400AsData) {
            return parseAsJson ? response.json() : response.text();
          }
          // На 5xx попробуем ретрай с экспоненциальной паузой
          if (response.status >= 500 && response.status < 600 && attempt < DEFAULT_RETRY_ATTEMPTS - 1) {
            await delay(2 ** attempt * 500);
            continue;
          }
          const error = await response.json().catch(() => ({ message: 'Network response was not ok' }));
          throw new Error(error.message || `HTTP ${response.status}`);
        }

        return parseAsJson ? response.json() : response.text();
      } catch (error: any) {
        lastError = error;
        // Ошибки сети, таймауты и отмены — пытаемся ретраить
        const isAbort = error?.name === 'AbortError';
        const isNetwork = error instanceof TypeError || /NetworkError/i.test(String(error));
        if ((isAbort || isNetwork) && attempt < DEFAULT_RETRY_ATTEMPTS - 1) {
          await delay(2 ** attempt * 500);
          continue;
        }
        throw error;
      }
    }
    throw lastError;
  }

  private buildUrl(endpoint: string): string {
    return `${API_URL}${endpoint}`;
  }

  private baseRequest(method: HttpMethod, endpoint: string, body?: any, parseAsJson = true, options?: { treat400AsData?: boolean, dropIfOffline?: boolean }): Promise<any> {
    const init: RequestInit = {
      method,
      headers: buildHeaders(),
      body: body !== undefined ? JSON.stringify(body) : undefined
    };
    return this.perform(this.buildUrl(endpoint), init, parseAsJson, options);
  }

  get(endpoint: string): Promise<any> {
    return this.baseRequest('GET', endpoint);
  }

  post(endpoint: string, data: any): Promise<any> {
    // Для POST сохраняем старую семантику: 400 возвращает тело ответа, а не исключение
    return this.baseRequest('POST', endpoint, data, true, { treat400AsData: true });
  }

  put(endpoint: string, data: any): Promise<any> {
    return this.baseRequest('PUT', endpoint, data);
  }

  delete(endpoint: string): Promise<any> {
    return this.baseRequest('DELETE', endpoint);
  }

  private async flushQueue(): Promise<void> {
    if (this.offlineQueue.size === 0) return;
    const entries = Array.from(this.offlineQueue.values());
    this.offlineQueue.clear();
    for (const item of entries) {
      this.perform(item.url, item.init, item.parseAsJson).then(item.resolve).catch(item.reject);
    }
  }
}

export const httpClient = new HttpClient();


